import { Component, computed, effect, inject, Input, signal, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { OrderType, SearchViewModel } from '@models/module/grid';
import { Permission } from '@models/permission';
import {
  NewScheduledDialogData,
  ScheduledEmail,
  ScheduledEmailActionEvent,
  ScheduledEmailActionsEnum,
  ScheduledEmailRow,
  ScheduledEmailTableOptions,
  ScheduleFrequencyEnum,
} from '@module-instance/scheduled-emails/interfaces';
import { NewScheduleDialogComponent } from '@module-instance/scheduled-emails/new-schedule-dialog/new-schedule-dialog.component';
import { ScheduledEmailService } from '@module-instance/scheduled-emails/scheduled-emails.service';
import { FiltersToApply, LocalFilterService, TablePaginator } from '@shared/components/data-list/common';
import { DataListModule } from '@shared/components/data-list/data-list.module';
import { DEFAULT_INFINITE_PAGE, PAGE_SIZE_OPTIONS } from '@shared/consts';
import { ConfirmationDialog, ConfirmationDialogData } from '@shared/dialogs/confirmation-dialog.component';
import { NotificationService, UserService } from '@shared/services';
import { UserStore } from '@signal-store';

@Component({
  selector: 'app-scheduled-emails',
  templateUrl: './scheduled-emails.component.html',
  imports: [DataListModule, MatIconModule, TranslateModule, MatButtonModule],
})
export class ScheduledEmailsComponent {
  private userStore = inject(UserStore);
  private localFilterService = inject(LocalFilterService);
  private emailService = inject(ScheduledEmailService);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private notificationService = inject(NotificationService);
  private userService = inject(UserService);

  permissions = signal<Permission[]>([]);
  canCreate = computed(() => this.permissions().includes(Permission.Create));
  canUpdate = computed(() => this.permissions().includes(Permission.Update));
  isInfiniteScroll = computed(() => this.userStore.userData().loadMore);

  @Input('permissions')
  set _permissions(permissions: Permission[]) {
    this.permissions.set(permissions);
  }

  tableColumns = signal(this.emailService.getTableColumns());
  tableData = signal<ScheduledEmailRow[]>(undefined);
  updatedRows = signal<ScheduledEmailRow[]>([]);
  options = signal<ScheduledEmailTableOptions>({
    actions: (row: ScheduledEmailRow) => this.emailService.getTableActions(row, this.permissions()),
    serverPaginationSort: true,
    sort: { active: 'updatedOn', direction: 'desc' },
    clickableRows: true,
    pagination: {
      pageIndex: 0,
      pageSize: this.isInfiniteScroll() ? DEFAULT_INFINITE_PAGE : PAGE_SIZE_OPTIONS.default[2],
      pageSizeOptions: PAGE_SIZE_OPTIONS.default,
    },
  });

  filters = signal(this.emailService.getFilters());
  filtersToApply = signal<FiltersToApply>({});

  constructor() {
    effect(() => {
      // trigger loadScheduledEmails when filters change
      const filters = this.filtersToApply();
      untracked(() => this.loadScheduledEmails());
    });
  }

  loadScheduledEmails(pageIndex = 0) {
    const options = this.options();
    const filters = this.filtersToApply();
    this.tableData.set(undefined);
    const searchParams: SearchViewModel = {
      page: pageIndex + 1,
      pagesize: options.pagination.pageSize,
      filter: this.localFilterService.mapLocalFilters(filters.filters),
      criteria: filters.criteria,
      sort: options.sort && [
        {
          field: options.sort.active,
          order: options.sort.direction === 'asc' ? OrderType.Ascending : OrderType.Descending,
        },
      ],
    };
    this.emailService.getScheduledEmails(searchParams).subscribe(({ data, records }) => {
      this.tableData.set(this.mapTableData(data));
      this.options.update(ops => {
        ops.pagination.length = records;
        ops.pagination.pageIndex = pageIndex;
        return { ...ops };
      });
    });
  }

  mapTableData(data: ScheduledEmail[]): ScheduledEmailRow[] {
    return data.map(dataRow => {
      const { scheduleFrequency } = dataRow;
      return {
        ...dataRow,
        // preserve original enum value for internal use
        scheduleFrequencyRaw: scheduleFrequency as ScheduleFrequencyEnum,
        // map enum to translated string for display, we need the same prop name for table column mapping on backend due to sorting
        scheduleFrequency: this.translate.instant(
          `SCHEDULED_EMAILS.frequency.${ScheduleFrequencyEnum[scheduleFrequency]}`,
        ),
      };
    });
  }

  onPaginationUpdated({ pageIndex, pageSize }: TablePaginator) {
    this.loadScheduledEmails(pageSize !== this.options().pagination.pageSize ? 0 : pageIndex);
  }

  addEditEntry(scheduledEmail?: ScheduledEmailRow) {
    if (scheduledEmail) {
      // scheduleFrequencyRaw is internal only, map it back to scheduleFrequency, as the dialog expects the enum value in scheduleFrequency
      scheduledEmail.scheduleFrequency = scheduledEmail.scheduleFrequencyRaw;
    }
    this.dialog
      .open<NewScheduleDialogComponent, NewScheduledDialogData, ScheduledEmail>(NewScheduleDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().md,
        data: {
          scheduledEmail,
          canUpdate: (scheduledEmail && this.canUpdate()) || (!scheduledEmail && this.canCreate()),
        },
      })
      .afterClosed()
      .pipe(filter(data => !!data))
      .subscribe(data => {
        if (scheduledEmail) {
          this.updatedRows.set(this.mapTableData([data]));
        } else {
          this.loadScheduledEmails();
        }
      });
  }

  performAction({ actionId, data }: ScheduledEmailActionEvent) {
    switch (actionId) {
      case ScheduledEmailActionsEnum.SEND_NOW:
        // Send now
        break;
      case ScheduledEmailActionsEnum.EDIT:
        this.addEditEntry(data);
        break;
      case ScheduledEmailActionsEnum.TOGGLE:
        this.emailService.toggleScheduled(data.id, !data.isEnabled).subscribe(updatedRow => {
          this.updatedRows.set(this.mapTableData([updatedRow]));
          this.notificationService.success('SCHEDULED_EMAILS.notifications.stateChanged');
        });
        break;
      case ScheduledEmailActionsEnum.DELETE:
        this.delete(data.id);
        break;
    }
  }

  delete(id: number): void {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'SCHEDULED_EMAILS.deleteDialog.title',
        messages: ['SCHEDULED_EMAILS.deleteDialog.message'],
      } as ConfirmationDialogData,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.emailService.deleteScheduledEmail(id).subscribe(() => {
          this.loadScheduledEmails();
          this.notificationService.success('SCHEDULED_EMAILS.notifications.deleted');
        });
      }
    });
  }
}
